import { useRef, useState } from 'react';
import { Send2 } from 'iconsax-react';
import { Paperclip, X } from 'lucide-react';

import { Button } from '@design-system/button';

export function Composer({
  onSend,
  busy,
  readOnlyNote,
}: {
  onSend: (text: string, files?: File[]) => void | Promise<void>;
  busy: boolean;
  /** Set when this conversation cannot be continued here: the box is read-only
   *  and this one line says where it continues instead. */
  readOnlyNote?: string;
}) {
  const readOnly = Boolean(readOnlyNote);
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const submitting = useRef(false);

  const submit = async () => {
    const t = text.trim();
    if ((!t && !files.length) || busy || readOnly || submitting.current) return;
    submitting.current = true;
    setError('');
    // The box empties the moment the message is accepted, not when the reply
    // finishes streaming: onSend resolves at the end of the stream, and until
    // then the sent text sat in the box as if it had not gone. A failed send
    // puts the text and files back — unless the user has started typing again.
    const sent = files;
    setText('');
    setFiles([]);
    try {
      await onSend(t, sent);
    } catch (err) {
      setText((current) => current || t);
      setFiles((current) => (current.length ? current : sent));
      setError(
        err instanceof Error ? err.message : 'Unable to send. Please try again.'
      );
    } finally {
      submitting.current = false;
    }
  };

  return (
    <div className='p-3'>
      {readOnlyNote && (
        <p
          data-slot='composer-read-only-note'
          className='mb-2 text-center text-xs text-gray-600'
        >
          {readOnlyNote}
        </p>
      )}
      {files.length > 0 && (
        <div className='mb-2 flex flex-wrap gap-2'>
          {files.map((file, index) => (
            <span
              key={`${file.name}-${index}`}
              className='inline-flex max-w-full items-center gap-1 rounded-lg bg-primary-50 px-2 py-1 text-xs'
            >
              <Paperclip className='size-3 shrink-0' />
              <span className='truncate'>{file.name}</span>
              <button
                type='button'
                disabled={busy}
                aria-label={`Remove ${file.name}`}
                onClick={() =>
                  setFiles((current) => current.filter((_, i) => i !== index))
                }
              >
                <X className='size-3' />
              </button>
            </span>
          ))}
        </div>
      )}
      {error && (
        <p
          role='alert'
          className='mb-2 text-xs text-red-600'
        >
          {error}
        </p>
      )}
      <input
        ref={fileInput}
        type='file'
        accept='.pdf,application/pdf'
        multiple
        className='sr-only'
        aria-label='Choose PDFs'
        disabled={busy || readOnly}
        onChange={(event) => {
          const next = [...files, ...Array.from(event.target.files ?? [])];
          event.target.value = '';
          if (next.some((file) => !file.name.toLowerCase().endsWith('.pdf'))) {
            setError('Choose a PDF file.');
          } else if (
            next.length > 3 ||
            next.reduce((sum, file) => sum + file.size, 0) > 10 * 1024 * 1024
          ) {
            setError('Attach up to 3 PDFs, totaling 10 MB or less.');
          } else {
            setFiles(next);
            setError('');
          }
        }}
      />
      {/* Figma Chat Field: pill, min 56px, soft purple shadow. Mic deferred. */}
      <div
        data-slot='tia-composer-field'
        className='flex min-h-14 items-center gap-2 rounded-full border border-white/30
                   bg-white/20 px-3 py-2 shadow-[0_1px_16px_rgba(124,90,187,0.25)]'
      >
        <Button
          size='iconSm'
          variant='ghost'
          disabled={busy || readOnly}
          onClick={() => fileInput.current?.click()}
          aria-label='Attach PDF'
          title='Attach PDFs (up to 40 pages each, 10 MB total)'
          className='shrink-0 text-gray-800'
        >
          <Paperclip className='size-6!' />
        </Button>
        <textarea
          aria-label='Message TIA'
          disabled={busy}
          readOnly={readOnly}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={1}
          placeholder='Ask TIA Anything..'
          className='max-h-32 min-h-8 flex-1 resize-none bg-transparent py-1 text-base
                     leading-8 tracking-[-0.4px] text-gray-1000 outline-none
                     placeholder:text-gray-600/50 focus:outline-none focus:ring-0'
          onKeyDown={(e) => {
            if (
              e.key === 'Enter' &&
              !e.shiftKey &&
              !e.nativeEvent.isComposing
            ) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <Button
          size='iconSm'
          variant='ghost'
          disabled={busy || readOnly || (!text.trim() && !files.length)}
          onClick={submit}
          aria-label='Send'
          className='shrink-0 text-primary-1000 disabled:text-gray-400'
        >
          <Send2
            size={24}
            variant='Bold'
          />
        </Button>
      </div>
    </div>
  );
}
